"use client";

import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useState } from "react";
export default function Integration() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: timelineRef,
    isVisible: timelineRefVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: screenshotRef,
    isVisible: screenshotVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: progressRef,
    isVisible: progressVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    if (progressVisible) {
      const timer = setTimeout(() => setProgressValue(70), 200);
      return () => clearTimeout(timer);
    }
  }, [progressVisible]);
  const steps = [{
    number: "01",
    title: "Discovery Call",
    description: "We talk through your school's current assessment setup and where marks are being lost, no commitment required.",
    duration: "30 minutes"
  }, {
    number: "02",
    title: "Pilot Scope & Agreement",
    description: "We agree which classes and subject area to start with, and what success looks like for your department.",
    duration: "1 week"
  }, {
    number: "03",
    title: "Teacher Onboarding",
    description: "Staff learn how to feed in assessments and read the diagnostic output, so Setura fits into how your department already works.",
    duration: "3 days"
  }, {
    number: "04",
    title: "Live in the Classroom",
    description: "Setura runs against the coursework, homework, or mock exams your students are already doing, becoming part of the existing routine rather than an extra task.",
    duration: "Ongoing"
  }, {
    number: "05",
    title: "Review & Decide",
    description: "You review the impact on your cohort and decide whether to expand Setura further.",
    duration: "End of term"
  }];
  return <section data-section-id="270" id="pilot-integration" className="relative overflow-hidden py-32 bg-background text-foreground">
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
        <div ref={headerRef} className={`max-w-3xl mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-xs font-mono uppercase tracking-[0.2em] mb-6 text-primary">
            The Integration Path
          </p>
          <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 text-foreground">
            From first conversation to{" "}
            <em className="italic text-primary">
              everyday classroom use.
            </em>
          </h2>
          <p className="font-default text-base lg:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            A structured path from your first conversation with us to Setura running against the work your students already produce. We fit into your existing assessment routine, not the other way around.
          </p>
        </div>

        <div ref={timelineRef} className="relative">
          <div className="hidden lg:block absolute top-[50%] left-0 right-0 h-px bg-border" />
          <div className="lg:hidden absolute top-0 bottom-0 left-6 w-px bg-border" />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-4 relative">
            {steps.map((step, i) => <div key={i} data-index={i} className={`relative transition-all duration-700 ease-out ${timelineRefVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                <div className="hidden lg:flex absolute top-[calc(50%-6px)] left-1/2 -translate-x-1/2 z-10" />

                <div className="lg:hidden absolute top-6 left-6 -translate-x-1/2 z-10">
                  <span className="block w-3 h-3 rounded-lg bg-primary shadow-lg" />
                </div>

                <Card className="lg:mb-12 ml-14 lg:ml-0 p-6 rounded-lg border bg-card hover:bg-accent transition-colors duration-300 gap-0 shadow-lg">
                  <div className="font-default text-5xl mb-4 leading-none text-primary">
                    {step.number}
                  </div>
                  <h3 className="font-default text-xl mb-3 text-foreground leading-snug">
                    {step.title}
                  </h3>
                  <p className="font-default text-sm text-muted-foreground leading-relaxed mb-5 line-clamp-2">
                    {step.description}
                  </p>
                  <div className="pt-4 border-t border-border">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary">
                      Duration: {step.duration}
                    </span>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        <div ref={screenshotRef} className={`mt-16 lg:mt-20 max-w-5xl mx-auto transition-all duration-1000 ease-out ${screenshotVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="overflow-hidden rounded-lg border bg-card shadow-lg p-0 gap-0">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card/60">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-lg bg-primary" />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Live Intake & Question Detection Preview
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary">
                Diagnostic Cycle
              </span>
            </div>
            <div className="relative aspect-[16/9] sm:aspect-[16/10] w-full overflow-hidden bg-muted/20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/02-browser-processing.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/02-browser-processing.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/02-browser-processing.webp-wvc-sizes" alt="Setura processing screen showing a paper being read and split into questions automatically." className="w-full h-full object-cover object-top" loading="lazy" decoding="async" />
            </div>
          </Card>
        </div>

        <div ref={progressRef} className={`mt-24 max-w-2xl mx-auto transition-all duration-1000 ease-out ${progressVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
              Average Deployment Time
            </span>
            <span className="font-default text-2xl text-foreground">14 days</span>
          </div>
          <Progress value={progressValue} className="h-1 bg-border [&>[data-slot=progress-indicator]]:bg-primary transition-all duration-[1500ms] ease-out" />
          <p className="font-default text-xs text-muted-foreground mt-4 text-center">
            From initial conversation to your first diagnostic report: most schools are fully operational in under two weeks.
          </p>
        </div>
      </div>
    </section>;
}